import axios from "axios";
import { arrType, datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";

export const SO_API = "/api/sales-order";
export const soUrlKey = "so_id";

export const masterEndpoints = {
	sites: `${SO_API}/masters/sites`,
	items: `${SO_API}/masters/items`,
};

const ynOptions = [
	{ name: "No", key: "N" },
	{ name: "Yes", key: "Y" },
];

export const mapMasterRow = (row) => {
	if (!row || typeof row !== "object") return row;
	if (row.key != null && row.name != null) return row;
	const code = row.code ?? row.CODE ?? row.key;
	const shortName = row.short_name ?? row.shrtname ?? row.SHRTNAME;
	const rawName = row.name ?? row.NAME ?? row.item_name ?? String(code ?? "");
	const name = shortName ? `${shortName} — ${rawName}` : rawName;
	return { ...row, key: code, name: name || String(code ?? ""), short_name: shortName || rawName };
};

export const parseItemRate = (value) => {
	if (value == null || value === "") return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

export const mapItemMasterRow = (row) => {
	const mapped = mapMasterRow(row);
	const code = mapped.key ?? mapped.code ?? mapped.item_code ?? mapped.barcode;
	const name = mapped.name || mapped.item_name || mapped.barcode || String(code ?? "");
	const rate = parseItemRate(mapped.rate ?? row?.rate ?? row?.RATE);
	return { ...mapped, key: code, name, rate };
};

/** When item_code is a search option object, flatten to code + rate from INVITEM. */
export const syncSoItemDraft = (draft) => {
	const next = { ...draft };
	const item = draft?.item_code;
	if (item && typeof item === "object") {
		next.item_code = item.key ?? item.code ?? item.item_code ?? "";
		const rate = parseItemRate(item.rate);
		next.rate = rate;
	} else if (item == null || item === "") {
		next.rate = null;
	}
	return next;
};

/** Same contract as PO: (offset, limit, searchTerm) → { data: { result } } for SearchInputFieldWithDebounce */
export const createSoMasterFetch = (endpoint, { minSearch = 0, limit: defaultLimit = 20, mapRow = mapMasterRow } = {}) => {
	return async (offset = 0, limit = defaultLimit, searchTerm = "") => {
		const q = String(searchTerm || "").trim();
		if (minSearch > 0 && q.length < minSearch) {
			return { data: { result: [] } };
		}
		const safeOffset = Number.isFinite(Number(offset)) && Number(offset) >= 0 ? Number(offset) : 0;
		const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : defaultLimit;
		const res = await axios.get(endpoint, {
			params: { q, search: q, offset: safeOffset, limit: safeLimit },
		});
		return {
			data: {
				result: (res.data?.result || []).map(mapRow),
			},
		};
	};
};

export const soMasterFetches = {
	owner_site_code: createSoMasterFetch(masterEndpoints.sites),
	destination_site_code: createSoMasterFetch(masterEndpoints.sites),
	item_code: createSoMasterFetch(masterEndpoints.items, { minSearch: 2, mapRow: mapItemMasterRow }),
};

const bqSearchColumn = (name, key, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.SELECT.key,
	data_type: datatypes.STRING.key,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
	use_bq_search: true,
	[selectOptionsKeyName]: { type: arrType, list: [] },
});

const enterColumn = (name, key, dataType = datatypes.STRING.key, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.ENTER.key,
	data_type: dataType,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
});

const selectColumn = (name, key, list, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.SELECT.key,
	data_type: datatypes.STRING.key,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
	[selectOptionsKeyName]: { type: arrType, list },
});

/** Matches Ginesys Create SO payload. order_no / intg = SO-{id} (from 10000); document_date = order_date. */
export const soHeaderColumnsObj = {
	owner_site_code: bqSearchColumn("Owner Site", "owner_site_code", { required: true }),
	destination_site_code: bqSearchColumn("Destination Site", "destination_site_code", { required: true }),
	order_date: enterColumn("Order Date", "order_date", datatypes.DATE.key, { required: true }),
	document_date: { ...enterColumn("Document Date", "document_date", datatypes.DATE.key), disabled: true },
	order_no: enterColumn("Order No", "order_no"),
	intg_order_id: enterColumn("Intg Order Id", "intg_order_id"),
	reservation_required: selectColumn("Reservation Required", "reservation_required", ynOptions),
	remarks: { ...enterColumn("Remarks", "remarks", datatypes.STRING.key), disabled: true },
	is_authorized: selectColumn("Authorized", "is_authorized", [
		{ name: "Yes", key: true },
		{ name: "No", key: false },
	]),
};

/** Selected view — includes system-generated order_no + document_date (same as order_date) */
export const soHeaderColumnList = [
	soHeaderColumnsObj.owner_site_code,
	soHeaderColumnsObj.destination_site_code,
	soHeaderColumnsObj.order_date,
	soHeaderColumnsObj.document_date,
	soHeaderColumnsObj.order_no,
	soHeaderColumnsObj.intg_order_id,
	soHeaderColumnsObj.reservation_required,
	soHeaderColumnsObj.remarks,
	soHeaderColumnsObj.is_authorized,
];

/** Create / edit — document_date + remarks shown disabled; system: order_no, intg, authorized */
export const soHeaderEditableColumnList = soHeaderColumnList.filter(
	(col) => col.key !== "is_authorized" && col.key !== "order_no" && col.key !== "intg_order_id"
);

/** document_date = order_date; remarks = owner site short name when site object is selected */
export const syncSoDocumentDate = (form) => {
	const next = {
		...form,
		document_date: form?.order_date ?? null,
	};
	const owner = form?.owner_site_code;
	if (owner && typeof owner === "object") {
		next.owner_site_code = owner.key ?? owner.code ?? null;
		next.remarks = owner.short_name || owner.name || "";
	} else if (owner == null || owner === "") {
		next.remarks = "";
	}
	return next;
};

export const soItemColumnsObj = {
	item_code: bqSearchColumn("Item / Barcode", "item_code", { required: true }),
	qty: enterColumn("Qty", "qty", datatypes.NUMBER.key, { required: true }),
	rate: { ...enterColumn("Rate", "rate", datatypes.NUMBER.key), disabled: true },
};

export const soItemColumnList = [soItemColumnsObj.item_code, soItemColumnsObj.qty, soItemColumnsObj.rate];

export const emptySoForm = () => ({
	owner_site_code: null,
	destination_site_code: null,
	order_date: null,
	document_date: null,
	intg_order_id: "",
	reservation_required: "N",
	remarks: "",
	is_authorized: false,
	items: [],
});

export const emptySoItemRow = () => ({
	item_code: "",
	qty: 1,
	rate: null,
});
